import React, { useEffect, useMemo, useState } from 'react';
import { AppButton } from '../AppButton/AppButton';
import { Bank, CaretLeft, CreditCard, PencilLine } from 'phosphor-react';
import { AppRadioButton } from '../AppRadioButton/AppRadioButton';
import { usePlaidLink } from 'react-plaid-link';
import { myFirebaseAuth } from '../../../firebase/config';
import type { PlaidLinkOnSuccess } from 'react-plaid-link';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { endProcessProgress, startProcessProgress } from '../../../redux/globalSlices/authSlice';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import axios from 'axios';
import {
  createSupplierPlaidPaymentMethod,
  getSupplierPaymentMethods,
  createPlaidPaymentMethod,
  getMainBuyerPaymentMethods,
  createStripeExternalBankToSupplierAccount,
  getStripeSupplierAccount,
  createStripePaymentMethod,
  createSupplierCustomerStripePaymentMethod,
  createFiservPaymentMethod,
  createSupplierCustomerFiservPaymentMethod,
} from '../../../redux/services/paymentsService';
import { Modal } from '@mui/material';
import './AppAddPaymentMethodModal.scss';
import { getPlaidLinkToken } from '../../../redux/services/authService';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { BASE_API_URL } from '../../../utils/constants';
import {
  Buyer,
  calculateStripePartialFacilitationFeesAndAmount,
  capitalizeWordsInSentence,
  DocumentsSummary,
  FiservCardDetails,
  getBuyerFiservFeeRate,
  getEffectiveCreditCardFeeWaiverConditions,
  hasCurrentDueWaiverCutoff,
  getOrdinalSuffix,
  Supplier,
} from '@dill/dill-shared';
import AppAddDwollaBankFundingSourceModal from './AppAddDwollaBankFundingSourceModal/AppAddDwollaBankFundingSourceModal';
import { useParamMainBuyerId } from '../../../utils/hooks/useParamMainBuyerId';
import { twMerge } from 'tailwind-merge';
import { openDillToast } from '../../../utils/helpers';
import type { Stripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import AppFiservCardInput, { FiservCardResult } from '../AppFiservCardInput/AppFiservCardInput';

const AppAddPaymentMethodModal = ({
  open,
  title,
  handleClose = () => {},
  userType = 'supplier',
  paymentOptionsToShow = 'ALL',
  buyerDetails,
  isDebitCardDisabled = false,
  isCreditCardDisabled = false,
  isBankDisabled = false,
  isPaymentMethodForWithdrawingFeesOnly = false,
}: {
  title?: string;
  buyerDetails?: {
    buyer: Buyer;
    usersEmailsDetails: {
      email: string;
      isInvitedUser: boolean;
      isStatementsEmailOptOut: boolean;
      isPaymentRemindersEmailOptOut: boolean;
    }[];
    documentsSummary: DocumentsSummary;
  } | null;
  open: boolean;
  handleClose: () => void;
  userType?: 'supplier' | 'buyer';
  paymentOptionsToShow?: 'ALL' | 'BANK_AND_FISERV_CARD' | 'FISERV_CARD' | 'CARD' | 'BANK';
  isDebitCardDisabled?: boolean;
  isCreditCardDisabled?: boolean;
  isBankDisabled?: boolean;
  isPaymentMethodForWithdrawingFeesOnly?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { selectedMainBuyer, selectedBuyerSuppliers } = useAppSelector((state) => state.buyers);
  const stripe: Stripe | null = useStripe();
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const elements: any = useElements();
  const { plaidTokenDetails, user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [paymentType, setPaymentType] = useState('bankTransfer');
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [plaidPublicToken, setPlaidPublicToken] = useState('');
  const [stripeBankToken, setStripeBankToken] = useState('');
  const [addingPayments, setAddingPayments] = useState(false);
  const [paymentMethodCreateErrorMessage, setPaymentMethodCreateErrorMessage] = useState('');
  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState('');
  const [selectedPlaidAccountDetails, setSelectedPlaidAccountDetails] = useState<{
    accountName: string;
    institutionName: string;
    bankAccountType: string;
    mask: string;
  } | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [isAddDwollaBankFundingSourceModalOpen, setIsAddDwollaBankFundingSourceModalOpen] =
    useState(false);
  const [verifiedFiservCardData, setVerifiedFiservCardData] = useState<FiservCardDetails | null>(
    null
  );
  const [selectedBankOption, setSelectedBankOption] = useState<'plaid' | 'dwolla' | null>(null);

  const { selectedSupplierForBuyer } = useAppSelector((state) => state.invoice);

  const buyerForMainBuyer = useMemo(() => {
    const buyer = user?.userBuyers?.find(
      (buy) =>
        buy.supplierId === selectedSupplierForBuyer?.id &&
        selectedMainBuyer &&
        selectedMainBuyer.quickBooksCustomerId === buy.quickbooksCustomerId
    );
    return buyer;
  }, [selectedMainBuyer, user?.userBuyers]);

  const effectiveWaiverConditions = useMemo(
    () =>
      getEffectiveCreditCardFeeWaiverConditions({
        supplier: selectedSupplierForBuyer,
        buyer: buyerForMainBuyer,
      }),
    [selectedSupplierForBuyer, buyerForMainBuyer]
  );

  // This modal advertises that a waiver exists at all — there is no invoice batch here to
  // evaluate it against — so any configured condition is enough to strike the fee through.
  const hasAnyWaiverConfigured =
    effectiveWaiverConditions.waiveForCurrentDuePayments === true ||
    effectiveWaiverConditions.waiveForDiscountDatePayments === true;

  useEffect(() => {
    setPaymentMethodCreateErrorMessage('');
    setAddingPayments(false);
    dispatch(getPlaidLinkToken({ isIncludeStatement: false }));
    return () => {};
  }, [dispatch]);
  useEffect(() => {
    if (open) {
      setPlaidPublicToken('');
      setStripeBankToken('');
      setPaymentMethodCreateErrorMessage('');
      setSelectedPlaidAccountDetails(null);
      setSelectedCustomerEmail('');
      setSelectedBankOption(null);
      setLinkToken(null);
      dispatch(getPlaidLinkToken({ isIncludeStatement: false }));
    }
    return () => {};
  }, [open]);

  useEffect(() => {
    if (plaidTokenDetails && plaidTokenDetails.link_token) {
      setLinkToken(plaidTokenDetails.link_token);
    }
    return () => {};
  }, [plaidTokenDetails]);

  const handlePlaidLinkSuccess: PlaidLinkOnSuccess = async (publicToken, metadata) => {
    setPaymentMethodCreateErrorMessage('');
    setSelectedPlaidAccountDetails({
      accountName: metadata.accounts[0].name,
      institutionName: metadata.institution?.name || '',
      mask: metadata.accounts[0].mask,
      bankAccountType: metadata.accounts[0]?.subtype || '',
    });
    // Exchange Plaid's Public Token for a Processor Token
    const token = await myFirebaseAuth.currentUser?.getIdToken();
    if (!token) {
      return null;
    }
    dispatch(startProcessProgress('getPlaidPublicToken'));
    const processorTokenResponse = await axios.post(
      `${BASE_API_URL}/paymentsAPI/plaid/publicToken`,
      { accountId: metadata.accounts[0].id, publicToken },
      {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      }
    );

    dispatch(endProcessProgress('getPlaidPublicToken'));
    if (processorTokenResponse.data && processorTokenResponse.data.success) {
      setPlaidPublicToken(processorTokenResponse.data?.data?.processorToken);
      setStripeBankToken(processorTokenResponse.data?.data?.stripeBankToken || '');
    }
  };
  const {
    open: openPlaidLink,
    ready: isPlaidLinkReady,
    error: plaidError,
  } = usePlaidLink({
    onSuccess: handlePlaidLinkSuccess,
    token: linkToken,
    onExit: (error) => {
      dispatch(getPlaidLinkToken({ isIncludeStatement: false }));
      if (error) {
        Sentry.captureException(error);
      }
    },
  });
  useEffect(() => {
    if (plaidError && open) {
      console.log({ plaidError });
      Sentry.captureException(plaidError);
      openDillToast({
        type: 'ERROR',
        message: `Failed to load plaid link: ${plaidError.message} `,
      });
    }

    return () => {};
  }, [plaidError]);

  useEffect(() => {
    if (buyerDetails) {
      if (userType === 'buyer') {
        const buyer = user?.userBuyers?.find(
          (buy) => selectedMainBuyer && selectedMainBuyer.buyerId === buy.id
        );
        const supplier = selectedBuyerSuppliers?.find((sup) => buyer?.supplierId === sup.id);
        if (supplier?.cardPaymentProvider === 'FISERV') {
          setPaymentType('fiservCreditCard');
        } else {
          setPaymentType('creditCard');
        }
      }
      if (userType === 'supplier') {
        if (currentSupplier?.cardPaymentProvider === 'FISERV') {
          setPaymentType('fiservCreditCard');
        } else {
          setPaymentType('creditCard');
        }
      }
    }
    return () => {};
  }, [buyerDetails, userType]);

  const paymentOptions = useMemo(() => {
    let options: { id: string; name: string }[] = [];
    let cardOptions = [
      { id: 'creditCard', name: 'Credit Card' },
      { id: 'debitOrOtherCard', name: 'Debit / Other Card' },
    ];
    let fiservCardOptions = [
      { id: 'fiservCreditCard', name: 'Credit Card' },
      { id: 'fiservDebitOrOtherCard', name: 'Debit / Other Card' },
    ];
    if (isDebitCardDisabled && !isCreditCardDisabled) {
      cardOptions = [cardOptions[0]];
      fiservCardOptions = [fiservCardOptions[0]];
    }
    if (!isDebitCardDisabled && isCreditCardDisabled) {
      cardOptions = [cardOptions[1]];
      fiservCardOptions = [fiservCardOptions[1]];
    }
    if (isDebitCardDisabled && isCreditCardDisabled) {
      cardOptions = [];
      fiservCardOptions = [];
    }
    const bankOptions = [{ id: 'bankTransfer', name: 'Bank Transfer' }];
    if (isBankDisabled) {
      options = [...cardOptions];
    } else if (paymentOptionsToShow === 'ALL') {
      options = [...bankOptions, ...cardOptions];
    } else if (paymentOptionsToShow === 'BANK') {
      options = [...bankOptions];
    } else if (paymentOptionsToShow === 'CARD') {
      options = [...cardOptions];
    } else if (paymentOptionsToShow === 'BANK_AND_FISERV_CARD') {
      options = [...bankOptions, ...fiservCardOptions];
    } else if (paymentOptionsToShow === 'FISERV_CARD') {
      options = [...fiservCardOptions];
    }

    return options;
  }, [paymentOptionsToShow, isDebitCardDisabled, isCreditCardDisabled, isBankDisabled]);
  const handlePlaid = () => {
    setPaymentMethodCreateErrorMessage('');
    openPlaidLink();
  };

  const handleAddPaymentMethod = async () => {
    setAddingPayments(true);
    if (!selectedPlaidAccountDetails?.accountName) {
      setPaymentMethodCreateErrorMessage('Failed to get bank name');
      setAddingPayments(false);
      return;
    }
    if (userType === 'supplier') {
      if (stripeBankToken) {
        await dispatch(
          createStripeExternalBankToSupplierAccount({ bankOrCardToken: stripeBankToken })
        );
        setStripeBankToken('');
        await dispatch(getStripeSupplierAccount());
      }
      const results = await dispatch(
        createSupplierPlaidPaymentMethod({
          supplierId: currentSupplier?.id || '',
          fundingSourceName: `${selectedPlaidAccountDetails.institutionName} ****${selectedPlaidAccountDetails.mask}`,
          plaidToken: plaidPublicToken,
          bankAccountType: selectedPlaidAccountDetails.bankAccountType,
          isPaymentMethodForWithdrawingFeesOnly,
        })
      );
      if (
        results.type === 'payments/createSupplierPlaidPaymentMethod/rejected' &&
        results.payload.errors
      ) {
        const errorMessageSplit = results.payload.errors[0].msg.split('id=');
        const errorMessage = errorMessageSplit[0];
        setPaymentMethodCreateErrorMessage(errorMessage);
      } else {
        setPlaidPublicToken('');
        setStripeBankToken('');
        await dispatch(getSupplierPaymentMethods({ supplierId: currentSupplier?.id || '' }));
        handleClose();
      }
    } else {
      if (stripeBankToken) {
        await dispatch(
          createStripeExternalBankToSupplierAccount({ bankOrCardToken: stripeBankToken })
        );
        setStripeBankToken('');
      }
      const buyerResults = await dispatch(
        createPlaidPaymentMethod({
          fundingSourceName: `${selectedPlaidAccountDetails.institutionName} ****${selectedPlaidAccountDetails.mask}`,
          plaidToken: plaidPublicToken,
          bankAccountType: selectedPlaidAccountDetails.bankAccountType,
        })
      );
      if (
        buyerResults.type === 'payments/createPlaidPaymentMethod/rejected' &&
        buyerResults.payload.errors
      ) {
        const errorMessageSplit = buyerResults.payload.errors[0].msg.split('id=');
        const errorMessage = errorMessageSplit[0];
        setPaymentMethodCreateErrorMessage(errorMessage);
      } else {
        setPlaidPublicToken('');
        setStripeBankToken('');
        await dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
        handleClose();
      }
    }
    setAddingPayments(false);
  };
  const handleCreateCreditCard = async (event: any) => {
    event.preventDefault();
    setPaymentMethodCreateErrorMessage('');
    if (!stripe || !elements) {
      return;
    }
    setAddingPayments(true);

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (error) {
      console.error(error);
      setPaymentMethodCreateErrorMessage(error?.message || '');
    } else {
      if (
        paymentType === 'creditCard' &&
        paymentMethod.card?.funding &&
        paymentMethod.card?.funding.toUpperCase() !== 'CREDIT'
      ) {
        setPaymentMethodCreateErrorMessage(
          'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.'
        );
        setAddingPayments(false);
        return;
      }
      if (
        paymentType === 'debitOrOtherCard' &&
        paymentMethod.card?.funding &&
        paymentMethod.card?.funding.toUpperCase() === 'CREDIT'
      ) {
        setPaymentMethodCreateErrorMessage(
          'The card entered is a credit card. Please change your payment method selection to Credit Card.'
        );
        setAddingPayments(false);
        return;
      }
      const response = await dispatch(
        createStripePaymentMethod({
          mainBuyerId: selectedMainBuyerId || '',
          paymentMethodId: paymentMethod.id,
          billingName: `${capitalizeWordsInSentence(paymentMethod?.card?.brand)} ****${
            paymentMethod?.card?.last4
          }`,
        })
      );

      if (response.type === 'payments/createStripePaymentMethod/fulfilled') {
        await dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
        handleClose();
      } else {
        if (response?.payload) {
          const message = response?.payload as string;
          setPaymentMethodCreateErrorMessage(message);
        }
      }
    }
    setAddingPayments(false);
  };

  const handleCreateSupplierCustomerCreditCard = async (event: any) => {
    event.preventDefault();
    setPaymentMethodCreateErrorMessage('');
    if (!stripe || !elements || !buyerDetails) {
      return;
    }
    if (!selectedCustomerEmail) {
      setPaymentMethodCreateErrorMessage('Customer email not selected');
      return;
    }
    setAddingPayments(true);

    const cardElement = elements.getElement(CardElement);

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (error) {
      console.error(error);
      setPaymentMethodCreateErrorMessage(error?.message || '');
    } else {
      if (
        paymentType === 'creditCard' &&
        paymentMethod.card?.funding &&
        paymentMethod.card?.funding.toUpperCase() !== 'CREDIT'
      ) {
        setPaymentMethodCreateErrorMessage(
          'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.'
        );
        setAddingPayments(false);
        return;
      }
      if (
        paymentType === 'debitOrOtherCard' &&
        paymentMethod.card?.funding &&
        paymentMethod.card?.funding.toUpperCase() === 'CREDIT'
      ) {
        setPaymentMethodCreateErrorMessage(
          'The card entered is a credit card. Please change your payment method selection to Credit Card.'
        );
        setAddingPayments(false);
        return;
      }
      const response = await dispatch(
        createSupplierCustomerStripePaymentMethod({
          supplierId: currentSupplier?.id || '',
          buyerId: buyerDetails.buyer.id || '',
          customerEmail: selectedCustomerEmail,
          paymentMethodId: paymentMethod.id,
          billingName: `${capitalizeWordsInSentence(paymentMethod?.card?.brand)} ****${
            paymentMethod?.card?.last4
          }`,
        })
      );

      if (response.type === 'payments/createSupplierCustomerStripePaymentMethod/fulfilled') {
        handleClose();
      } else {
        if (response?.payload) {
          const message = response?.payload as string;
          setPaymentMethodCreateErrorMessage(message);
        }
      }
    }
    setAddingPayments(false);
  };

  const handleCreateSupplierCustomerFiservCard = async (event: any) => {
    event.preventDefault();
    setPaymentMethodCreateErrorMessage('');
    if (!verifiedFiservCardData || !buyerDetails) {
      return;
    }
    if (!selectedCustomerEmail) {
      setPaymentMethodCreateErrorMessage('Customer email not selected');
      return;
    }
    setAddingPayments(true);
    if (
      paymentType === 'fiservCreditCard' &&
      verifiedFiservCardData.cardType.toUpperCase() !== 'CREDIT'
    ) {
      setPaymentMethodCreateErrorMessage(
        'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.'
      );
      setAddingPayments(false);
      return;
    }
    if (
      paymentType === 'fiservDebitOrOtherCard' &&
      verifiedFiservCardData.cardType.toUpperCase() === 'CREDIT'
    ) {
      setPaymentMethodCreateErrorMessage(
        'The card entered is a credit card. Please change your payment method selection to Credit Card.'
      );
      setAddingPayments(false);
      return;
    }
    const response = await dispatch(
      createSupplierCustomerFiservPaymentMethod({
        supplierId: currentSupplier?.id || '',
        buyerId: buyerDetails.buyer.id || '',
        customerEmail: selectedCustomerEmail,
        fiservCardDetails: verifiedFiservCardData,
      })
    );

    if (response.type === 'payments/createSupplierCustomerFiservPaymentMethod/fulfilled') {
      handleClose();
    } else {
      if (response?.payload) {
        const message = response?.payload as string;
        setPaymentMethodCreateErrorMessage(message);
      }
    }

    setAddingPayments(false);
  };
  const handleCardChange = (event: any) => {
    setCardComplete(event.complete);
  };

  const creditCardTakeRate = useMemo(() => {
    if (userType === 'supplier') {
      return 0;
    }
    const buyer = user?.userBuyers?.find(
      (buy) => selectedMainBuyer && selectedMainBuyer.buyerId === buy.id
    );
    let calculateAmount = {} as {
      supplierFees: number;
      buyerFees: number;
      amount: number;
      amountToPay: number;
      totalFees: number;
    };
    let calculationDetails = { amount: 100 } as {
      amount: number;
      buyer?: Buyer;
      supplier?: Supplier;
    };
    const supplier = selectedBuyerSuppliers?.find((sup) => buyer?.supplierId === sup.id);
    if (supplier && (supplier.cardPaymentProvider === 'STRIPE' || !supplier.cardPaymentProvider)) {
      if (buyer) {
        calculationDetails = {
          ...calculationDetails,
          buyer: { ...buyer, creditCardTakeRateExtra: 0 },
        };
        if (supplier) {
          calculationDetails = {
            ...calculationDetails,
            supplier: { ...supplier, creditCardTakeRateExtra: 0 },
          };
        }
      }
      calculateAmount = calculateStripePartialFacilitationFeesAndAmount({
        ...calculationDetails,
      });
      return calculateAmount?.buyerFees ?? 0;
    } else if (supplier && buyer && supplier.cardPaymentProvider === 'FISERV') {
      return getBuyerFiservFeeRate({ buyer, supplier });
    } else {
      return 0;
    }
  }, [open, selectedMainBuyer, selectedBuyerSuppliers]);

  const handleFiservCardResult = (result: FiservCardResult) => {
    if (result.success) {
      setVerifiedFiservCardData(result.data);
    } else {
      setVerifiedFiservCardData(null);
    }
  };

  const handleSaveFiservCard = async () => {
    if (!verifiedFiservCardData) {
      return;
    }

    if (
      paymentType === 'fiservCreditCard' &&
      verifiedFiservCardData.cardType.toUpperCase() !== 'CREDIT'
    ) {
      setPaymentMethodCreateErrorMessage(
        'The card entered is not a credit card. Please change your payment method selection to Debit / Other Card.'
      );
      setAddingPayments(false);
      return;
    }
    if (
      paymentType === 'fiservDebitOrOtherCard' &&
      verifiedFiservCardData.cardType.toUpperCase() == 'CREDIT'
    ) {
      setPaymentMethodCreateErrorMessage(
        'The card entered is a credit card. Please change your payment method selection to Credit Card.'
      );
      setAddingPayments(false);
      return;
    }
    setAddingPayments(true);
    const response = await dispatch(
      createFiservPaymentMethod({
        mainBuyerId: selectedMainBuyerId || '',
        fiservCardDetails: verifiedFiservCardData,
      })
    );
    setAddingPayments(false);

    if (response.type === 'payments/createFiservPaymentMethod/fulfilled') {
      await dispatch(getMainBuyerPaymentMethods({ mainBuyerId: selectedMainBuyerId || '' }));
      handleClose();
    } else {
      if (response?.payload) {
        const message = response?.payload as string;
        setPaymentMethodCreateErrorMessage(message);
      }
    }
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <div className="flex min-h-80 w-[90vw] md:w-[60vw] lg:w-[50vw] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col">
        <AppAddDwollaBankFundingSourceModal
          userType={userType}
          open={isAddDwollaBankFundingSourceModalOpen}
          isPaymentMethodForWithdrawingFeesOnly={isPaymentMethodForWithdrawingFeesOnly}
          handleClose={(isSuccessfullyAdded) => {
            setIsAddDwollaBankFundingSourceModalOpen(false);
            if (isSuccessfullyAdded) {
              handleClose();
            }
          }}
        />
        <div className="flex items-center mt-2">
          <AppButton
            type="FLAT"
            buttonStyles={{
              width: '40px',
              height: '40px',
              marginLeft: '12px',
              marginRight: '12px',
            }}
            icon={<CaretLeft size={24} color={'#000000'} />}
            onClick={handleClose}
            isDisabled={addingPayments}
          />

          <h3 className="font-bold text-xl">Add New Payment Method</h3>
        </div>

        <hr className="h-px my-1 bg-gray-200 border-0 dark:bg-gray-200"></hr>

        <div className="flex max-h-[70vh]  flex-col px-1 md:px-4 mt-4 grow overflow-y-scroll">
          <div className="flex flex-col">
            <div className="flex flex-col  justify-between">
              <p className="text-sm font-bold ml-4 mb-2">Select a Payment Method</p>
              {isPaymentMethodForWithdrawingFeesOnly && (
                <p className="text-xs font-semibold ml-4 mb-2 italic">
                  This payment method will only be used for withdrawing transaction fees.
                </p>
              )}
            </div>
            <ReactTooltip
              id={'waiver-tool-tip'}
              place="top"
              variant="dark"
              opacity={'100%'}
              style={{
                background: '#222',
                width: '300px',
                zIndex: 99999,
              }}>
              <span>
                Credit card fees will be waived for payments where all invoices are not past due
              </span>
            </ReactTooltip>
            <ReactTooltip
              id={'discount-date-waiver-tool-tip'}
              place="top"
              variant="dark"
              opacity={'100%'}
              style={{
                background: '#222',
                width: '300px',
                zIndex: 99999,
              }}>
              <span>
                Credit card fees will be waived for payments where all invoices are within the
                discount date
              </span>
            </ReactTooltip>
            <ReactTooltip
              id={'day-of-month-waiver-tool-tip'}
              place="top"
              variant="dark"
              opacity={'100%'}
              style={{
                background: '#222',
                width: '300px',
                zIndex: 99999,
              }}>
              <span>
                {`Credit card fees will be waived for payments where all invoices are not past due, as long as payment is made on or before the ${getOrdinalSuffix(
                  effectiveWaiverConditions.currentDueWaiverDayOfMonthCutoff ?? 0
                )} of the month`}
              </span>
            </ReactTooltip>

            {paymentOptions.map((option, i) => {
              return (
                <div
                  key={i + 'oo'}
                  className="w-full px-4 my-1"
                  data-tooltip-id={
                    hasAnyWaiverConfigured &&
                    creditCardTakeRate &&
                    creditCardTakeRate > 0 &&
                    option.id.toLowerCase().includes('credit')
                      ? effectiveWaiverConditions.waiveForCurrentDuePayments
                        ? hasCurrentDueWaiverCutoff(
                            effectiveWaiverConditions.currentDueWaiverDayOfMonthCutoff
                          )
                          ? 'day-of-month-waiver-tool-tip'
                          : 'waiver-tool-tip'
                        : 'discount-date-waiver-tool-tip'
                      : ''
                  }>
                  <button
                    className={
                      'flex flex-row w-full rounded-xl WHITE-BG px-5 py-4 border  items-center cursor-pointer ' +
                      (option.id === paymentType ? 'PRIMARY_500-BORDER' : ' GREY_300-BORDER')
                    }
                    onClick={() => {
                      setPaymentType(option.id);
                    }}>
                    <div className="flex mr-2 items-center">
                      <AppRadioButton isChecked={option.id === paymentType} />
                      <div className="payment-type-logo">
                        {option.name === 'Bank Transfer' ? (
                          <Bank size={30} style={{ display: 'flex', alignSelf: 'center' }} />
                        ) : (
                          <CreditCard size={30} style={{ display: 'flex', alignSelf: 'center' }} />
                        )}
                      </div>
                    </div>
                    <div className="flex justify-between w-full items-center">
                      <div className="flex flex-col">
                        <p className="text-sm font-bold text-left">{option.name}</p>
                      </div>
                      {(option.id === 'creditCard' || option.id === 'fiservCreditCard') &&
                      creditCardTakeRate &&
                      creditCardTakeRate > 0 ? (
                        <div className="flex-end">
                          <div className="text-sm TEXT_SECONDARY-CLR ">Fees</div>
                          <div
                            className={twMerge(
                              'text-xs PRIMARY_500-CLR',
                              hasAnyWaiverConfigured ? 'line-through' : ''
                            )}>{`${creditCardTakeRate}%`}</div>
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </button>
                </div>
              );
            })}

            {paymentType === 'bankTransfer' &&
              paymentOptions.map((po) => po.id).includes('bankTransfer') && (
                <div className="flex flex-col p-4 mt-2">
                  <div className="flex flex-col md:flex-row gap-2">
                    <div
                      className={twMerge(
                        'rounded-xl p-4 cursor-pointer border-2 transition-all duration-200',
                        'bg-white',
                        !isPlaidLinkReady
                          ? 'opacity-50 cursor-not-allowed border-gray-300'
                          : selectedBankOption === 'plaid'
                          ? 'border-blue-600 shadow-sm'
                          : 'border-gray-300 hover:border-blue-400 hover:shadow-md'
                      )}
                      onClick={() => {
                        if (isPlaidLinkReady) {
                          setSelectedBankOption('plaid');
                          handlePlaid();
                        }
                      }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 pr-2 md:pr-3">
                          <p className="text-sm md:text-sm font-semibold mb-1">
                            I have access to my online banking credentials
                          </p>
                          <p className="text-xs text-gray-500 mb-2">
                            Connect instantly via Plaid secure link
                          </p>
                        </div>
                        <div className={twMerge('flex-shrink-0 transition-colors text-blue-600')}>
                          <Bank size={24} className="sm:size-10 md:size-20" />
                        </div>
                      </div>
                      {isPlaidLinkReady ? (
                        <p className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-700">
                          Instant verification
                        </p>
                      ) : (
                        <p className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium text-gray-400">
                          <span className="w-3 h-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                          Loading...
                        </p>
                      )}
                    </div>

                    <div
                      className={twMerge(
                        'rounded-xl p-4 cursor-pointer border-2 transition-all duration-200',
                        'bg-white',
                        selectedBankOption === 'dwolla'
                          ? 'border-blue-600 shadow-sm'
                          : 'border-gray-300 hover:border-blue-400 hover:shadow-md'
                      )}
                      onClick={() => {
                        setSelectedBankOption('dwolla');
                        setIsAddDwollaBankFundingSourceModalOpen(true);
                      }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 pr-2 md:pr-3">
                          <p className="text-sm md:text-sm font-semibold mb-1">
                            I do not have access to my online banking credentials
                          </p>
                          <p className="text-xs text-gray-500 mb-2">
                            Manually enter your bank account details
                          </p>
                        </div>
                        <div className={twMerge('flex-shrink-0 transition-colors text-blue-600')}>
                          <PencilLine size={24} className="sm:size-10 md:size-20" />
                        </div>
                      </div>
                      <p className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700">
                        Verify micro-deposits after 1 to 2 days wait period (one-time setup)
                      </p>
                    </div>
                  </div>
                  {selectedPlaidAccountDetails && (
                    <div className="mt-4">
                      <p className="text-sm mb-1 font-bold">Selected bank details</p>
                      <p className="text-xs mb-1">
                        {`${selectedPlaidAccountDetails.accountName} from ${selectedPlaidAccountDetails.institutionName}`}
                      </p>
                      <p className="text-xs mb-1 text-red-500 mt-2">
                        Please make sure to select "Add" to finish adding your payment method on
                        Dill
                      </p>
                    </div>
                  )}
                </div>
              )}
            {(paymentType === 'creditCard' ||
              paymentType === 'debitOrOtherCard' ||
              paymentType === 'fiservCreditCard' ||
              paymentType === 'fiservDebitOrOtherCard') &&
              buyerDetails &&
              buyerDetails?.usersEmailsDetails && (
                <div className="flex flex-col p-4 PRIMARY_50-BG mt-2">
                  <p className="font-bold text-sm">Select user email for receipt</p>
                  {buyerDetails?.usersEmailsDetails.map((details, index) => {
                    return (
                      <div
                        key={`oo${index}`}
                        className={twMerge(
                          'text-sm border my-0.5 py-1 px-2 cursor-pointer hover:bg-blue-300 rounded-lg',
                          details.email === selectedCustomerEmail ? 'bg-blue-400 ' : ''
                        )}
                        onClick={() => {
                          setSelectedCustomerEmail(details.email);
                        }}>
                        {details.email}
                      </div>
                    );
                  })}
                </div>
              )}
            {(paymentType === 'creditCard' || paymentType === 'debitOrOtherCard') && (
              <div className="flex flex-col p-4 PRIMARY_50-BG mt-2">
                <form>
                  <label className="text-xs mb-1" htmlFor="card-element">
                    {/* Card details: */}
                    <CardElement
                      id="card-element"
                      onChange={handleCardChange}
                      options={{ disableLink: true }}
                    />
                  </label>
                </form>
              </div>
            )}

            {(paymentType === 'fiservCreditCard' || paymentType === 'fiservDebitOrOtherCard') && (
              <div className="flex">
                <div className="mx-0.5 w-full">
                  <AppFiservCardInput
                    onResult={handleFiservCardResult}
                    isWarnCardToBeSaved={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col w-full  mb-3">
          <hr className="h-px my-2 bg-gray-200 border-0 dark:bg-gray-200"></hr>
          {paymentMethodCreateErrorMessage && (
            <p className="text-xs mb-3 mx-3 text-center  ERROR_500-CLR">
              {paymentMethodCreateErrorMessage}
            </p>
          )}
          <div className="flex flex-row self-end ">
            <AppButton
              text={'Close'}
              type="TERTIARY"
              buttonStyles={{ marginLeft: '10px', marginRight: '10px' }}
              onClick={() => {
                handleClose();
              }}
              isDisabled={addingPayments}
            />

            {(paymentType === 'fiservCreditCard' || paymentType === 'fiservDebitOrOtherCard') &&
            !buyerDetails ? (
              <AppButton
                text="Create"
                buttonStyles={{ marginLeft: '10px', marginRight: '10px', height: '37px' }}
                onClick={handleSaveFiservCard}
                isDisabled={!verifiedFiservCardData}
                isLoading={addingPayments}
              />
            ) : (paymentType === 'fiservCreditCard' || paymentType === 'fiservDebitOrOtherCard') &&
              buyerDetails ? (
              <AppButton
                text="Create"
                buttonStyles={{ marginLeft: '10px', marginRight: '10px', height: '37px' }}
                onClick={handleCreateSupplierCustomerFiservCard}
                isDisabled={!verifiedFiservCardData}
                isLoading={addingPayments}
              />
            ) : (paymentType === 'creditCard' || paymentType === 'debitOrOtherCard') &&
              !buyerDetails ? (
              <AppButton
                text="Create"
                buttonStyles={{ marginLeft: '10px', marginRight: '10px', height: '37px' }}
                onClick={handleCreateCreditCard}
                // isDisabled={formIsValid}
                isLoading={addingPayments}
              />
            ) : (paymentType === 'creditCard' || paymentType === 'debitOrOtherCard') &&
              buyerDetails ? (
              <AppButton
                text="Create"
                buttonStyles={{ marginLeft: '10px', marginRight: '10px', height: '37px' }}
                onClick={handleCreateSupplierCustomerCreditCard}
                // isDisabled={formIsValid}
                isLoading={addingPayments}
              />
            ) : (
              <AppButton
                text="Add"
                buttonStyles={{ marginLeft: '10px', marginRight: '10px', height: '37px' }}
                onClick={handleAddPaymentMethod}
                isDisabled={!plaidPublicToken}
                isLoading={addingPayments}
              />
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AppAddPaymentMethodModal;
